import "./ConsultantForms.scss";
import InfoContext from "pages/InfoContext";
import InputSearchTag from "core/inputs/input-search/InputSearchTag";
import InputSelect from "core/inputs/input-select/InputSelect";
import InputText from "core/inputs/input-text/InputText";
import { Controller } from "utils/Controller";
import { FormattedMessage } from "react-intl";
import {
  CANADA_BRANCH,
  CANADA_CONTRACT,
  CONTRACT_TYPES,
  CONTRACTUAL_STATUS,
  FILTERS_FOR_NAMES,
  MODAL_CONTENT,
  TAG_TYPES,
  VAR_TYPES,
} from "constants/constants";
import Icon from "core/icon/Icon";
import { Typography } from "core/typography/Typography";
import { useContext, useEffect, useState } from "react";
import {
  CONSULTANT_FORM_LABELS,
  CONSULTANT_FORM_PLACEHOLDERS,
  JOB_CATEGORY_LABEL,
  FORM_INFO,
  AFFECT_MANAGER_LABEL,
  CONTRACT_TYPE_OPTIONS,
  STAFF_FORM_LABELS,
  STAFF_FORM_PLACEHOLDERS,
} from "constants/forms";
import {
  displaySalary,
  formatUsersOptions,
  renderContractType,
  renderInputArrivalDate,
  renderInputBranch,
  renderInputCegid,
  renderInputCoefPos,
  renderInputContractStatus,
  renderInputEmail,
  renderInputEndContract,
  renderInputJob,
  renderInputJobCategory,
  renderInputReasonLeave,
  renderInputStartContract,
  renderPersonalManagement,
} from "../CommonInputs";
import { useParams } from "react-router-dom";
import _ from "lodash";
import PropTypes from "prop-types";
import { tabletSize } from "utils/screenSizes";
import { getFilteredList } from "utils/consultantHelper";
import { getTranslatedList, getCellErrorHelper } from "utils/helper";
import { useJobCategoryCascade } from "utils/customHooks";
import { useGeneralContext } from "api/directory/general/GeneralProvider";
import {
  ROLE_GP,
  MISSION_MANAGER_ROLES,
  ROLE_RGN_ASSIST,
  ROLE_RGP,
} from "access-roles/roles";

const ConsultantCorporateInfoForm = ({
  control,
  errors,
  readOnly = false,
  register,
  setValue,
  watch,
  clearErrors,
  cellHighlight,
}) => {
  const {
    branches,
    currencyList,
    davFunctions,
    lang,
    syntecCoef,
    consultantBySlug,
    contractualStatusList,
    apiErrorResponse,
    leavingReasons,
    currencyListByBranchGetter,
    currencyListByBranch,
    setCurrencyListByBranch,
    isDisabledCell,
    setStatus,
    setCode,
    setOpenModalIntent,
  } = useContext(InfoContext);

  const renderPencilButton = (modalIntent, fieldId) => {
    if (!modalIntent || readOnly || !consultantBySlug) return null;
    return (
      <button
        type="button"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          if (setOpenModalIntent) {
            setOpenModalIntent(modalIntent);
          }
        }}
        data-testid={`${fieldId}-edit-icon`}
        style={{
          position: "absolute",
          top: 6,
          right: 6,
          zIndex: 1,
          background: "none",
          border: "none",
          padding: 4,
          cursor: "pointer",
          pointerEvents: "auto",
        }}
      >
        <Icon
          icon="pencil"
          color="violet"
          width={18}
          height={18}
          onClick={() => {}}
        />
      </button>
    );
  };
  const { slug } = useParams();
  const pencilWrapperStyle = { position: `${slug ? "relative" : undefined}` };
  const { getNamesByFilters, namesByFilters } = useGeneralContext();
  const { domain_category_id, domain_id, jobCategoryList, jobOptions } =
    useJobCategoryCascade({
      watch,
      setValue,
      clearErrors,
      jobs: davFunctions,
    });

  const [minimumSalary, setMinimumSalary] = useState(null);
  const [isMinimalSalaryReached, setIsMinimalSalaryReached] = useState(true);

  const branch_id = watch("branch_id", []);
  const manager_id = watch("manager_id", []);
  const gp_id = watch("gp_id", []);
  const contractual_status_id = watch("contractual_status_id", null);
  const syntec_coef_position = watch("syntec_coef_position", []);
  const comment_salary = watch("dashboard_comment", []);
  const salary = watch("salary", null);
  const contract_type = watch("contract_type", []);
  const start_date = watch("start_date", "");
  const [contractTypesList, setContractTypesList] = useState([]);
  const leaving_reason_id = watch("leaving_reason_id", []);
  const currency_id = watch("currency_id", null);

  useEffect(() => {
    const list =
      branch_id[0]?.label === CANADA_BRANCH
        ? CONTRACT_TYPE_OPTIONS
        : getFilteredList({
            list: CONTRACT_TYPE_OPTIONS,
            value: CANADA_CONTRACT,
          });
    setContractTypesList(list);
    if (!slug)
      branch_id[0]?.value
        ? currencyListByBranchGetter(branch_id[0]?.value)
        : setCurrencyListByBranch([]);
  }, [branch_id, slug]);

  useEffect(() => {
    !_.isEmpty(start_date) && setValue("arrival_date", start_date);
  }, [start_date]);

  useEffect(() => {
    setIsMinimalSalaryReached(true);
    setMinimumSalary(
      syntecCoef.find((elm) => elm.id === syntec_coef_position[0]?.value)
        ?.gross_conventional_minimum
    );
    contractual_status_id?.length > 0 &&
      salary &&
      setIsMinimalSalaryReached(salary >= minimumSalary);
  }, [salary, contractual_status_id, syntec_coef_position]);

  useEffect(() => {
    !!salary && errors["salary"] && clearErrors("salary");
  }, [contractual_status_id]);

  useEffect(() => {
    setValue(
      "currency_id",
      currencyListByBranch?.length > 0 ? currencyListByBranch[0]?.id : null
    );
  }, [currencyListByBranch]);

  const renderInputSalary = ({ errors = {}, errorMessage, errorValue }) => (
    <Controller
      {...{
        control,
        register,
        name: "salary",
        rules: {
          required: true,
          validate: !isMinimalSalaryReached && {
            minimumSalary: (salary) => salary >= minimumSalary,
          },
        },
        render: (props) => (
          <InputText
            comment={comment_salary}
            commentName={"dashboard_comment"}
            commentSettings={FORM_INFO.salary.comment_salary}
            errors={errors["salary"] || apiErrorResponse?.["salary"]}
            label={CONSULTANT_FORM_LABELS.salary}
            placeholder={CONSULTANT_FORM_PLACEHOLDERS.salary}
            readOnly={readOnly}
            required
            setComment={(val) => setValue("dashboard_comment", val)}
            type={VAR_TYPES.NUMBER}
            errorMessage={errorMessage}
            errorValue={errorValue}
            {...props}
          />
        ),
      }}
    />
  );

  useEffect(() => {
    if (consultantBySlug) {
      if (!watch("salary.currency_id") && currencyList?.length > 0) {
        const defaultCurrency =
          currencyList.find((c) => c.id === 1) || currencyList[0];
        setValue("salary.currency_id", defaultCurrency.id);
      }
    }
  }, [currencyList, salary]);

  const renderInputCurrency = () => (
    <Controller
      {...{
        control,
        register,
        name: consultantBySlug ? "salary.currency_id" : "currency_id",
        rules: {
          required: true,
        },
        render: (props) => (
          <InputSelect
            id="currency_id"
            maxHeight={160}
            label={CONSULTANT_FORM_LABELS.currency_id}
            placeholder={CONSULTANT_FORM_PLACEHOLDERS.currency_id}
            options={currencyListByBranch.map((elm) => ({
              label: elm.currency_symbol,
              value: elm.id,
            }))}
            required
            setValue={setValue}
            readOnly={readOnly}
            errorMessage={
              apiErrorResponse?.["currency_id"] ||
              errors?.["currency_id"]?.message
            }
            errors={errors["currency_id"] || apiErrorResponse?.["currency_id"]}
            {...props}
          />
        ),
      }}
    />
  );

  const renderInputAffectManager = ({ getNamesByFilters, namesByFilters }) => (
    <div
      className={`cell-padding ${isDisabledCell} ${getCellErrorHelper({
        hasValue: watch("manager_id")?.length > 0,
        hasError:
          apiErrorResponse?.["manager_id"] || apiErrorResponse?.["__all__"],
        cellHighlight,
      })}`}
      style={pencilWrapperStyle}
    >
      {renderPencilButton(MODAL_CONTENT.MANAGE_MANAGERS, "manager_id")}
      <Controller
        {...{
          control,
          register,
          name: "manager_id",
          rules: {
            required: true,
          },
          render: (props) => (
            <InputSearchTag
              errors={
                errors["manager_id"] ||
                apiErrorResponse?.["manager_id"] ||
                apiErrorResponse?.["__all__"]
              }
              errorMessage={
                apiErrorResponse?.["manager_id"] ||
                apiErrorResponse?.["__all__"] ||
                errors?.["manager_id"]?.message
              }
              getOptions={(search) =>
                getNamesByFilters({
                  role: MISSION_MANAGER_ROLES,
                  types: [FILTERS_FOR_NAMES.PERSON],
                  search,
                })
              }
              label={AFFECT_MANAGER_LABEL}
              limitChoice={FORM_INFO.manager.limitChoice}
              minLength={2}
              onDelete={() => setValue("manager_id", [])}
              options={formatUsersOptions({
                users: namesByFilters,
                type: TAG_TYPES.PEOPLE,
              })}
              placeholder={CONSULTANT_FORM_PLACEHOLDERS.manager_id}
              popup={FORM_INFO.manager.popup}
              readOnly={readOnly}
              required
              selectedOptions={manager_id}
              setValue={(val) => setValue("manager_id", val)}
              type={TAG_TYPES.PEOPLE}
              id="manager"
              {...props}
            />
          ),
        }}
      />
    </div>
  );

  useEffect(() => {
    !slug && setValue("syntec_coef_position", []);
  }, [contractual_status_id, slug]);

  const renderLine2 = () => (
    <>
      <div
        className={`cell-padding ${getCellErrorHelper({
          hasValue: watch("gp_id")?.length > 0,
          hasError: apiErrorResponse?.["gp_id"],
          cellHighlight,
        })}`}
      >
        {renderPersonalManagement({
          control,
          errors: errors["gp_id"] || apiErrorResponse?.["gp_id"],
          getOptions: (search) =>
            getNamesByFilters({
              role: [ROLE_GP, ROLE_RGP, ROLE_RGN_ASSIST],
              types: [FILTERS_FOR_NAMES.PERSON],
              search: search,
            }),
          options: formatUsersOptions({
            users: namesByFilters,
            type: TAG_TYPES.PEOPLE,
          }),
          label: CONSULTANT_FORM_LABELS.gp_id,
          placeholder: CONSULTANT_FORM_PLACEHOLDERS.gp_id,
          readOnly,
          register,
          setValue,
          value: gp_id,
          errorMessage:
            apiErrorResponse?.["gp_id"] || errors?.["gp_id"]?.message,
        })}
      </div>
      {slug &&
        contractualStatusList?.find(
          (elm) => elm?.en === CONTRACTUAL_STATUS.EXECUTIVE
        )?.id === contractual_status_id && (
          <div
            className={`cell-padding ${isDisabledCell} ${getCellErrorHelper({
              hasValue: watch("syntec_coef_position")?.length > 0,
              hasError: apiErrorResponse?.["syntec_coef_position"],
              cellHighlight,
            })}`}
            style={pencilWrapperStyle}
          >
            {renderPencilButton(
              MODAL_CONTENT.MANAGE_POSITION_COEF,
              "syntec_coef_position_line2"
            )}
            {renderInputCoefPos({
              contractual_status_id,
              control,
              required: contractual_status_id === 2,
              errors:
                errors["syntec_coef_position"] ||
                apiErrorResponse?.["syntec_coef_position"],
              label: STAFF_FORM_LABELS.syntec_coef_position_staff,
              placeholder:
                STAFF_FORM_PLACEHOLDERS.syrenderInputCoefPosntec_coef_position_staff,
              readOnly,
              register,
              setValue,
              name: "syntec_coef_position",
              syntecCoef,
              value: syntec_coef_position,
              errorMessage:
                apiErrorResponse?.["syntec_coef_position"] ||
                errors?.["syntec_coef_position"]?.message,
            })}
          </div>
        )}
      {!_.isEmpty(slug) && (
        <div
          className={`cell-padding ${isDisabledCell}`}
          style={pencilWrapperStyle}
        >
          {renderPencilButton(
            MODAL_CONTENT.MANAGE_WORK_CONTRACT,
            "arrival_date"
          )}
          {renderInputArrivalDate({
            control,
            errors,
            label: CONSULTANT_FORM_LABELS.arrival_date_at_Davidson,
            placeholder: CONSULTANT_FORM_PLACEHOLDERS.arrival_date,
            readOnly,
            register,
            lang,
            setValue,
            value: watch("arrival_date"),
          })}
        </div>
      )}
      {!_.isEmpty(slug) && (
        <>
          <div
            className={`cell-padding ${isDisabledCell}`}
            style={pencilWrapperStyle}
          >
            {renderPencilButton(MODAL_CONTENT.MANAGE_WORK_CONTRACT, "end_date")}
            {renderInputEndContract({
              control,
              errors,
              label: STAFF_FORM_LABELS.end_date,
              readOnly,
              register,
              lang,
              contract_type,
              setValue,
              value: watch("end_date"),
            })}
          </div>
          <div
            className={`cell-padding ${isDisabledCell}`}
            style={pencilWrapperStyle}
          >
            {renderPencilButton(
              MODAL_CONTENT.MANAGE_WORK_CONTRACT,
              "leaving_reason_id"
            )}
            {renderInputReasonLeave({
              control,
              errors,
              readOnly,
              register,
              setValue,
              watch,
              leavingReasons,
              leaving_reason_id,
              lang,
            })}
          </div>
        </>
      )}
    </>
  );
  const renderLine1 = () => (
    <>
      <div
        className={`cell-padding ${isDisabledCell} ${getCellErrorHelper({
          hasValue: watch("branch_id")?.length > 0,
          hasError: apiErrorResponse?.["branch_id"] || errors?.["branch_id"],
          cellHighlight,
        })}`}
        style={pencilWrapperStyle}
      >
        {renderPencilButton(MODAL_CONTENT.MANAGE_WORK_CONTRACT, "branch_id")}
        {renderInputBranch({
          control,
          register,
          branches,
          readOnly,
          errors: errors["branch_id"] || apiErrorResponse?.["branch_id"],
          label: { id: "branch", defaultMessage: "Filiale" },
          placeholder: CONSULTANT_FORM_PLACEHOLDERS.branch_id,
          value: branch_id,
          setValue,
          errorMessage:
            apiErrorResponse?.["branch_id"] || errors?.["branch_id"]?.message,
        })}
      </div>
      <div
        className={`cell-padding ${isDisabledCell} ${getCellErrorHelper({
          hasValue: watch("contract_type")?.length > 0,
          hasError: apiErrorResponse?.["contract_type"],
          cellHighlight,
        })}`}
        style={pencilWrapperStyle}
      >
        {renderPencilButton(
          MODAL_CONTENT.MANAGE_WORK_CONTRACT,
          "contract_type"
        )}
        {renderContractType({
          control,
          errors:
            errors["contract_type"] || apiErrorResponse?.["contract_type"],
          label: CONSULTANT_FORM_LABELS.contract_type,
          placeholder: CONSULTANT_FORM_PLACEHOLDERS.contract_type,
          readOnly,
          register,
          setValue,
          options: contractTypesList,
          errorMessage:
            apiErrorResponse?.["contract_type"] ||
            errors?.["contract_type"]?.message,
        })}
      </div>
      <div
        className={`cell-padding ${isDisabledCell} ${getCellErrorHelper({
          hasValue: domain_category_id,
          hasError: apiErrorResponse?.["domain_category_id"],
          cellHighlight,
        })}`}
      >
        {renderInputJobCategory({
          control,
          jobCategories: jobCategoryList,
          errors,
          label: JOB_CATEGORY_LABEL,
          lang,
          readOnly: !consultantBySlug ? readOnly : true,
          register,
          setValue,
          errorMessage:
            apiErrorResponse?.["domain_category_id"] ||
            errors?.["domain_category_id"]?.message,
          clearErrors,
        })}
      </div>
      <div
        className={`cell-padding ${isDisabledCell} ${getCellErrorHelper({
          hasValue: watch("domain_id")?.length > 0,
          hasError: apiErrorResponse?.["domain_id"],
          cellHighlight,
        })}`}
        style={pencilWrapperStyle}
      >
        {renderPencilButton(MODAL_CONTENT.MANAGE_JOB_HISTORY, "domain_id")}
        {renderInputJob({
          control,
          davFunctions: jobOptions,
          errors: errors["domain_id"] || apiErrorResponse?.["domain_id"],
          label: CONSULTANT_FORM_LABELS.domain_id,
          lang,
          placeholder: CONSULTANT_FORM_PLACEHOLDERS.domain_id,
          readOnly: consultantBySlug ? true : readOnly,
          // The category comes first: it is what narrows this list
          // (redmine 12939). Only reachable at creation — on an existing
          // sheet both fields are already read-only.
          lockedUntilCategory: !consultantBySlug && !domain_category_id,
          register,
          setValue,
          value: domain_id,
          errorMessage:
            apiErrorResponse?.["domain_id"] || errors?.["domain_id"]?.message,
        })}
      </div>
      {consultantBySlug && (
        <>
          <div
            className={`cell-padding ${isDisabledCell}`}
            style={pencilWrapperStyle}
          >
            {renderPencilButton(MODAL_CONTENT.MANAGE_SALARY, "salary")}
            {displaySalary({
              control,
              register,
              readOnly,
            })}
          </div>
          <div
            className={`cell-padding ${isDisabledCell}`}
            style={pencilWrapperStyle}
          >
            {renderPencilButton(MODAL_CONTENT.MANAGE_SALARY, "currency_id")}
            {renderInputCurrency()}
          </div>
        </>
      )}
    </>
  );

  return (
    <>
      <div className="consultant_corporateinfo_create">
        <div className="left__div">
          <Typography
            className="title-3 violet text-uppercase"
            type="h2"
            label={
              <FormattedMessage
                id="corporate_info"
                defaultMessage="Corporate info"
              />
            }
          />
          {slug ? (
            <>
              <div className="line-1-id">
                <div
                  className={`cell-padding ${getCellErrorHelper({
                    hasValue: watch("email")?.length > 0,
                    cellHighlight,
                  })}`}
                >
                  {renderInputEmail({
                    control,
                    errors: errors["email"] || apiErrorResponse?.["email"],
                    label: CONSULTANT_FORM_LABELS.email,
                    placeholder: CONSULTANT_FORM_PLACEHOLDERS.email,
                    readOnly,
                    register,
                    errorMessage:
                      apiErrorResponse?.["email"] || errors?.["email"]?.message,
                    copyEmail: slug,
                    setCode,
                    setStatus,
                  })}
                </div>
                {renderLine1()}
                {renderInputAffectManager({
                  getNamesByFilters: getNamesByFilters,
                  namesByFilters: namesByFilters,
                })}
              </div>
              <div className={`line-2`}>
                <div className={"cell-padding"}>
                  {renderInputCegid({
                    control,
                    errors,
                    label: CONSULTANT_FORM_LABELS.cegid_id,
                    placeholder: CONSULTANT_FORM_PLACEHOLDERS.cegid_id,
                    readOnly,
                    register,
                  })}
                </div>
                {renderLine2()}
              </div>
            </>
          ) : (
            <>
              <div className="line-1 ">
                {renderLine1()}
                <div
                  className={`cell-padding ${getCellErrorHelper({
                    hasValue: slug
                      ? isMinimalSalaryReached
                      : watch("salary")?.length > 0,
                    cellHighlight,
                  })}`}
                >
                  {renderInputSalary({
                    errors,
                    errorValue: minimumSalary,
                    errorMessage:
                      apiErrorResponse?.["salary"] ||
                      (!isMinimalSalaryReached &&
                        "add_consultant_minimum_salary_not_reached") ||
                      errors?.["salary"]?.message,
                  })}
                </div>
                <div
                  className={`cell-padding ${
                    !slug && !currency_id ? "cell-disabled" : ""
                  } ${getCellErrorHelper({
                    hasValue: watch("currency_id"),
                    cellHighlight,
                  })}`}
                >
                  {renderInputCurrency()}
                </div>
                {tabletSize &&
                  renderInputAffectManager({
                    getNamesByFilters: getNamesByFilters,
                    namesByFilters: namesByFilters,
                  })}
              </div>
              <div className={`line-2`}>
                {!tabletSize &&
                  renderInputAffectManager({
                    getNamesByFilters: getNamesByFilters,
                    namesByFilters: namesByFilters,
                  })}
                {!slug && (
                  <div
                    className={`cell-padding ${getCellErrorHelper({
                      hasValue: watch("start_date"),
                      cellHighlight,
                    })}`}
                  >
                    {renderInputStartContract({
                      control,
                      errors,
                      label: CONSULTANT_FORM_LABELS.start_date,
                      placeholder: CONSULTANT_FORM_PLACEHOLDERS.start_date,
                      readOnly,
                      register,
                      lang,
                      setValue,
                      value: watch("start_date"),
                    })}
                  </div>
                )}
                <div
                  className={`cell-padding ${getCellErrorHelper({
                    hasValue: watch("arrival_date"),
                    cellHighlight,
                  })}`}
                >
                  {renderInputArrivalDate({
                    control,
                    errors,
                    label: CONSULTANT_FORM_LABELS.arrival_date,
                    placeholder: CONSULTANT_FORM_PLACEHOLDERS.arrival_date,
                    readOnly,
                    register,
                    lang,
                    setValue,
                    value: watch("arrival_date"),
                  })}
                </div>
                <div
                  className={`cell-padding ${getCellErrorHelper({
                    hasValue:
                      [
                        CONTRACT_TYPES.CDI,
                        CONTRACT_TYPES.REPRESENTATIVE,
                      ].includes(watch("contract_type")) || watch("end_date"),
                    hasError: apiErrorResponse?.["end_date"],
                    cellHighlight,
                  })}`}
                >
                  {renderInputEndContract({
                    control,
                    errors,
                    label: STAFF_FORM_LABELS.end_date,
                    readOnly,
                    register,
                    lang,
                    contract_type,
                    setValue,
                    value: watch("end_date"),
                  })}
                </div>
                <div className="cell-padding">
                  {renderInputReasonLeave({
                    control,
                    errors,
                    leavingReasons,
                    lang,
                    leaving_reason_id,
                    setValue,
                    watch,
                    readOnly,
                    register,
                  })}
                </div>
                {renderLine2()}
              </div>
              <div className="line-3">
                <div
                  className={`cell-padding ${isDisabledCell} ${getCellErrorHelper(
                    {
                      hasValue: watch("contractual_status_id"),
                      hasError: !!apiErrorResponse?.["contractual_status_id"],
                      cellHighlight,
                    }
                  )}`}
                >
                  {renderInputContractStatus({
                    control,
                    errors,
                    label: CONSULTANT_FORM_LABELS.contractual_status_id,
                    placeholder:
                      CONSULTANT_FORM_PLACEHOLDERS.contractual_status_id,
                    readOnly,
                    register,
                    setValue,
                    options: getTranslatedList({
                      lang,
                      list: contractualStatusList,
                    }),
                    errorMessage:
                      apiErrorResponse?.["contractual_status_id"] ||
                      errors?.["contractual_status_id"]?.message,
                  })}
                </div>
                {contractualStatusList?.find((elm) => elm?.en === "Executive")
                  ?.id === contractual_status_id && (
                  <div
                    className={`coef cell-padding ${isDisabledCell} ${getCellErrorHelper(
                      {
                        hasValue: watch("syntec_coef_position")?.length > 0,
                        cellHighlight,
                      }
                    )}`}
                    style={pencilWrapperStyle}
                  >
                    {renderPencilButton(
                      MODAL_CONTENT.MANAGE_POSITION_COEF,
                      "syntec_coef_position_line3"
                    )}
                    {renderInputCoefPos({
                      contractual_status_id,
                      control,
                      required: contractual_status_id === 2,
                      errors:
                        errors["syntec_coef_position"] ||
                        apiErrorResponse?.["syntec_coef_position"],
                      label: STAFF_FORM_LABELS.syntec_coef_position_staff,
                      placeholder:
                        STAFF_FORM_PLACEHOLDERS.syrenderInputCoefPosntec_coef_position_staff,
                      readOnly,
                      register,
                      setValue,
                      name: "syntec_coef_position",
                      syntecCoef,
                      value: syntec_coef_position,
                      errorMessage:
                        apiErrorResponse?.["syntec_coef_position"] ||
                        errors?.["syntec_coef_position"]?.message,
                    })}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
        <br />
      </div>
      <br />
    </>
  );
};

export default ConsultantCorporateInfoForm;

ConsultantCorporateInfoForm.propTypes = {
  control: PropTypes.object,
  errors: PropTypes.object,
  readOnly: PropTypes.bool,
  register: PropTypes.func,
  setValue: PropTypes.func,
  unregister: PropTypes.func,
  watch: PropTypes.func,
  clearErrors: PropTypes.func,
  cellHighlight: PropTypes.bool,
};
